// api-client/errors.ts
/**
 * @file errors.ts
 * @description Defines custom error classes for the ApiClient.
 * These errors provide a consistent, library-agnostic way to handle issues
 * arising from API requests.
 */

/**
 * Base class for all custom errors originating from the ApiClient.
 * It includes an optional `originalError` property for debugging purposes.
 */
export class ApiError extends Error {
  /** The original error object, useful for debugging underlying issues. */
  public readonly originalError?: any;
  /** Discriminator property to easily identify errors from this client. */
  public readonly isApiClientError: boolean = true;

  constructor(message: string, originalError?: any) {
    super(message);
    this.name = this.constructor.name; // Ensures correct error name
    this.originalError = originalError;
    // Maintains proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Represents an error due to a request timeout.
 */
export class TimeoutError extends ApiError {
  constructor(message = 'Request timed out', originalError?: any) {
    super(message, originalError);
  }
}

/**
 * Represents an error due to network issues (e.g., connectivity problems).
 */
export class NetworkError extends ApiError {
  constructor(message = 'A network error occurred', originalError?: any) {
    super(message, originalError);
  }
}

/**
 * Contextual information for a REST API error response.
 */
export interface RestApiResponseErrorContext {
  status: number;
  data: any;
  headers: Record<string, string | string[] | undefined>;
}

/**
 * Represents an error specific to a REST API request,
 * often including details from the HTTP response.
 */
export class RestApiError extends ApiError {
  public readonly response?: RestApiResponseErrorContext;

  constructor(
    message: string,
    response?: RestApiResponseErrorContext,
    originalError?: any,
  ) {
    super(message, originalError);
    this.response = response;
  }
}

/**
 * Contextual information for a GraphQL API error response.
 */
export interface GraphQLResponseErrorContext {
  status?: number; // HTTP status of the response
  data?: any; // Parsed response data, if available
  errors?: Array<{ message: string; [key: string]: any }>; // GraphQL errors array
}

/**
 * Represents an error specific to a GraphQL API request,
 * often including GraphQL-specific error details.
 */
export class GraphQLApiError extends ApiError {
  public readonly response?: GraphQLResponseErrorContext;

  constructor(
    message: string,
    response?: GraphQLResponseErrorContext,
    originalError?: any,
  ) {
    super(message, originalError);
    this.response = response;
  }
}
